import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Plus, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { submitDeal, fetchPublicCategories, formatApiErrorDetail } from "@/lib/api";

const SOURCES = ["Amazon", "MediaMarkt", "Otto", "Zalando", "Saturn", "Lidl", "Aldi", "DM", "Sonstige"];

const Field = ({ label, children }) => (
  <div>
    <label className="block text-sm font-semibold text-slate-700 font-worksans mb-1.5">{label}</label>
    {children}
  </div>
);

const inputCls = "w-full bg-slate-100 rounded-lg px-3.5 py-2.5 text-sm font-worksans outline-none focus:ring-2 focus:ring-[#A259FF]/40 transition-all";

export default function SubmitDeal() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [cats, setCats] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    product_name: "", category: "", source: "Amazon", original_price: "",
    deal_price: "", image_url: "", product_url: "", voucher_code: "", description: "",
  });

  useEffect(() => {
    if (!loading && !user) navigate("/login?next=/deal-einreichen");
  }, [loading, user, navigate]);

  useEffect(() => {
    fetchPublicCategories().then((d) => setCats(d.categories.map((c) => c.category))).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.product_name || !form.category || !form.deal_price) {
      toast.error("Bitte Produkt, Kategorie und Deal-Preis ausfüllen");
      return;
    }
    setBusy(true);
    try {
      await submitDeal({
        product_name: form.product_name,
        category: form.category,
        source: form.source,
        original_price: parseFloat(form.original_price || form.deal_price),
        deal_price: parseFloat(form.deal_price),
        image_url: form.image_url,
        product_url: form.product_url,
        voucher_code: form.voucher_code || null,
        description: form.description,
      });
      toast.success("Deal eingereicht! Er erscheint nach Freigabe. 🪄");
      navigate("/profil");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Einreichen fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl px-4 sm:px-6 py-8" data-testid="submit-deal-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-11 w-11 rounded-xl ai-gradient-bg flex items-center justify-center">
          <Plus className="h-6 w-6 text-white" strokeWidth={2.5} />
        </div>
        <div>
          <h1 className="font-outfit font-black text-2xl tracking-tight text-slate-900">Deal einreichen</h1>
          <p className="text-sm text-slate-500 font-worksans">Teile dein Schnäppchen mit der Community</p>
        </div>
      </div>

      <form onSubmit={onSubmit} className="bg-white rounded-2xl border border-slate-200 p-5 sm:p-6 space-y-4">
        <Field label="Produktname *">
          <input className={inputCls} value={form.product_name} onChange={set("product_name")} placeholder="z.B. Sony WH-1000XM5 Kopfhörer" data-testid="submit-product-name" />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Kategorie *">
            <input list="cats" className={inputCls} value={form.category} onChange={set("category")} placeholder="Elektronik" data-testid="submit-category" />
            <datalist id="cats">{cats.map((c) => <option key={c} value={c} />)}</datalist>
          </Field>
          <Field label="Händler">
            <select className={inputCls} value={form.source} onChange={set("source")} data-testid="submit-source">
              {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Originalpreis (€)">
            <input type="number" step="0.01" className={inputCls} value={form.original_price} onChange={set("original_price")} placeholder="419.00" data-testid="submit-original-price" />
          </Field>
          <Field label="Deal-Preis (€) *">
            <input type="number" step="0.01" className={inputCls} value={form.deal_price} onChange={set("deal_price")} placeholder="289.00" data-testid="submit-deal-price" />
          </Field>
        </div>
        <Field label="Bild-URL">
          <input className={inputCls} value={form.image_url} onChange={set("image_url")} placeholder="https://…" data-testid="submit-image-url" />
        </Field>
        <Field label="Link zum Deal">
          <input className={inputCls} value={form.product_url} onChange={set("product_url")} placeholder="https://amazon.de/…" data-testid="submit-product-url" />
        </Field>
        <Field label="Gutscheincode (optional)">
          <input className={inputCls} value={form.voucher_code} onChange={set("voucher_code")} placeholder="ZAUBER10" data-testid="submit-voucher" />
        </Field>
        <Field label="Beschreibung">
          <textarea rows={3} className={inputCls} value={form.description} onChange={set("description")} placeholder="Warum ist das ein guter Deal?" data-testid="submit-description" />
        </Field>

        <button type="submit" disabled={busy} className="w-full ai-gradient-bg text-white font-outfit font-bold rounded-xl px-6 py-3.5 hover:opacity-90 active:scale-95 transition-all inline-flex items-center justify-center gap-2" data-testid="submit-deal-button">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Sparkles className="h-5 w-5" />}
          Deal einreichen
        </button>
      </form>
    </div>
  );
}
